import { useEffect, useState } from 'react';
import { Link } from 'react-router-dom';
import { useSiteCopyText, useWellnessPlacement } from '../hooks/useWellnessSite';

interface WellnessAdminBannerProps {
  pendingVerifications?: number;
  compact?: boolean;
}

/** Warm wellness banner for the admin dashboard */
export function WellnessAdminBanner({
  pendingVerifications = 0,
  compact = false,
}: WellnessAdminBannerProps) {
  const landingSlides = useWellnessPlacement('landing');
  const slides = landingSlides.length > 0 ? landingSlides : [];
  const [active, setActive] = useState(0);
  const adminHeadline = useSiteCopyText('admin.dashboard.headline');
  const adminSubline = useSiteCopyText('admin.dashboard.subline');

  useEffect(() => {
    if (slides.length < 2) return;
    const id = window.setInterval(() => {
      setActive((i) => (i + 1) % slides.length);
    }, 8000);
    return () => window.clearInterval(id);
  }, [slides.length]);

  const current = slides[active];

  return (
    <section className={`wellness-admin-banner ${compact ? 'wellness-admin-banner--compact' : ''}`}>
      <div className="wellness-admin-banner-ambient" aria-hidden>
        <span className="wellness-admin-banner-orb wellness-admin-banner-orb-1" />
        <span className="wellness-admin-banner-orb wellness-admin-banner-orb-2" />
      </div>

      {slides.map((slide, i) => (
        <div
          key={slide.id}
          className={`wellness-admin-banner-bg ${i === active ? 'is-active' : ''}`}
          style={{ backgroundImage: `url(${slide.imageUrl})` }}
          aria-hidden
        />
      ))}

      <div className="wellness-admin-banner-overlay" aria-hidden />
      <div className="wellness-admin-banner-shimmer" aria-hidden />

      <div className="wellness-admin-banner-content">
        <div className="flex flex-wrap items-center gap-2">
          <span className="admin-live-pill">
            <span className="admin-live-dot" aria-hidden />
            Live
          </span>
        </div>
        <h1 className="wellness-admin-banner-headline">{adminHeadline}</h1>
        <p className="wellness-admin-banner-subline">{adminSubline}</p>

        {!compact && current?.caption ? (
          <p className="wellness-admin-banner-caption">{current.caption}</p>
        ) : null}

        <div className="wellness-admin-banner-actions">
          {pendingVerifications > 0 ? (
            <Link to="/therapists/pending" className="admin-banner-chip admin-banner-chip-alert">
              {pendingVerifications} pending verification{pendingVerifications === 1 ? '' : 's'}
            </Link>
          ) : null}
          <Link to="/wellness" className="admin-banner-chip">
            Wellness &amp; heroes
          </Link>
          <Link to="/content" className="admin-banner-chip">
            Psychoeducation
          </Link>
        </div>
      </div>

      {slides.length > 1 ? (
        <div className="wellness-admin-banner-dots" aria-hidden>
          {slides.map((slide, i) => (
            <span
              key={slide.id}
              className={`wellness-admin-banner-dot ${i === active ? 'is-active' : ''}`}
            />
          ))}
        </div>
      ) : null}
    </section>
  );
}
